import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import { AlertSeverityEnum, Presentacion, PresentacionPagingRes, UseMutationsParams } from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';
import { getUrlParamsFetch, handleAxiosError } from '@/shared';

const { get, post, put } = erpApi();

///* tanSctack query
export const useFetchPresentacion = ({
  page = 1,
  page_size = 10,
  nombre = '',
}: GetPresentacionParams) => {
  return useQuery({
    queryKey: ['presentacion', page, nombre, page_size],
    queryFn: async () => {
      return getPresentacion({ page, nombre, page_size });
    },
  });
};

export const useGetPresentaciones = (id: string) => {
  return useQuery({
    queryKey: ['presentacion', id],
    queryFn: async () => {
      return getPresentaciones(id);
    },
  });
};

export const useCreatePresentacion = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: createPresentacion,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['presentacion'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Presentación creada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useUpdatePresentacion = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: updatePresentacion,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['presentacion'] });
      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Presentación actualizada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

///* axios
export type GetPresentacionParams = Partial<Presentacion> & {
  page?: number;
  page_size?: number;
  nombre?: string;
};

export type CreatePresentacionParams = Omit<Presentacion, 'id_presentacion'>;
export type UpdatePresentacionParams = {
  id: string;
  data: CreatePresentacionParams;
};

export const getPresentacion = async (params?: GetPresentacionParams) => {
  const queryParams = getUrlParamsFetch({ ...params });
  return get<PresentacionPagingRes>(
    `${urlBackend}api/v1/presentacion/?${queryParams}`,
    true
  );
};


export const getPresentaciones = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await get<Presentacion>(`${urlBackend}api/v1/presentacion/${id}/`, true);

  res.status === 'error' && setAlert(true, res.message, res.status);
  return res;
};

export const createPresentacion = async (data: CreatePresentacionParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return post<Presentacion>(`${urlBackend}api/v1/presentacion/`, data, true);
};

export const updatePresentacion = async ({ id, data }: UpdatePresentacionParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return put<Presentacion>(`${urlBackend}api/v1/presentacion/${id}/`, data, true);
};
